// src/pages/News.js
// ──────────────────────────────────────────────────────────────────────────
// News & events. Cards expand into a full-story reader (multiple paragraphs)
// and visitors can leave a like (remembered on their device).
// Replace NEWS with real posts when ready.
// ──────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { fetchNews } from '../lib/cms';
import theme from '../theme/theme';

const LIKES_KEY = 'mti_news_liked';

const Header = styled.header`
  background: ${theme.color.blueDeep}; color: #fff;
  padding: 7rem ${theme.layout.gutter} 5rem;
  .inner { max-width: ${theme.layout.maxWidth}; margin: 0 auto; }
  h1 { font-weight: 400; font-size: clamp(2.4rem, 6vw, 4rem); color: #fff; margin-top: 1.2rem; }
  p { margin-top: 1rem; max-width: 50ch; font-weight: 300; color: rgba(255,255,255,0.82); }
  .kicker { color: #fff; }
`;
const Grid = styled.section`
  max-width: ${theme.layout.maxWidth}; margin: 0 auto; padding: 5rem ${theme.layout.gutter};
  display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1.8rem;
`;
const Card = styled.article`
  border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius};
  overflow: hidden; background: #fff; display: flex; flex-direction: column; cursor: pointer;
  transition: transform 0.3s ${theme.ease}, box-shadow 0.3s ${theme.ease};
  &:hover { transform: translateY(-4px); box-shadow: 0 22px 44px -22px rgba(11,42,99,0.35); }
  .thumb { aspect-ratio: 16/10; background: ${theme.color.paperAlt} url(${(p) => p.$src}) center/cover; }
  .b { padding: 1.3rem 1.4rem 1.5rem; display: flex; flex-direction: column; gap: 0.5rem; flex: 1; }
  .date { font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: ${theme.color.accent}; font-weight: 700; }
  h3 { font-weight: 500; font-size: 1.3rem; line-height: 1.15; }
  p { font-size: 0.92rem; color: ${theme.color.inkSoft}; margin: 0; flex: 1; }
  .foot { display: flex; align-items: center; justify-content: space-between; margin-top: 0.6rem; }
  .more { font-size: 0.82rem; font-weight: 700; color: ${theme.color.accent}; }
`;

const Like = styled.button`
  display: inline-flex; align-items: center; gap: 6px; background: none; border: none; cursor: pointer;
  font-family: ${theme.font.body}; font-size: 0.85rem; color: ${(p) => (p.$on ? '#d6336c' : theme.color.inkSoft)};
  .heart { font-size: 1rem; }
  &:hover { color: #d6336c; }
`;

// Reader modal
const Overlay = styled(motion.div)`
  position: fixed; inset: 0; z-index: 1000; background: rgba(8,24,60,0.6);
  display: flex; align-items: flex-start; justify-content: center; padding: 4vh 4vw; overflow-y: auto;
`;
const Reader = styled(motion.article)`
  width: min(760px, 100%); background: #fff; border-radius: ${theme.layout.radius}; overflow: hidden;
  box-shadow: 0 40px 90px -30px rgba(0,0,0,0.6);
  .hero { aspect-ratio: 16/8; background: ${theme.color.paperAlt} url(${(p) => p.$src}) center/cover; position: relative; }
  .x { position: absolute; top: 14px; right: 16px; width: 38px; height: 38px; border-radius: 50%; border: none;
       background: rgba(8,24,60,0.55); color: #fff; font-size: 1.3rem; cursor: pointer; }
  .body { padding: 2.2rem clamp(1.4rem, 4vw, 2.8rem) 2.6rem; }
  .date { font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: ${theme.color.accent}; font-weight: 700; }
  h2 { font-weight: 400; font-size: clamp(1.8rem, 4vw, 2.6rem); margin: 0.5rem 0 1.4rem; }
  .body p { color: ${theme.color.ink}; line-height: 1.75; margin: 0 0 1.1rem; }
  .reactions { margin-top: 1.6rem; padding-top: 1.4rem; border-top: 1px solid ${theme.color.line}; display: flex; align-items: center; gap: 1rem; }
`;

const SEED_NEWS = [
  {
    id: 'enrolment-open', date: 'Admissions', title: 'Applications are open all year round', likes: 12,
    image: 'https://images.pexels.com/photos/3760930/pexels-photo-3760930.jpeg?auto=compress&cs=tinysrgb&w=1600',
    excerpt: 'Join a hands-on skills course in Agriculture, Engineering, Hospitality or ICT — start where you are.',
    body: [
      'At Mbala Training Institute, learning never has to wait for a single intake date. Applications run throughout the year, so whether you finished school last week or last decade, there is always a place to begin.',
      'Our courses are 80% practical. From your very first weeks you are in the workshop, the kitchen, the field or at the computer — building the muscle memory that employers and customers actually pay for.',
      'If you are unsure which course fits you, our team will sit with you, talk through your goals and your funding options, and help you choose a path. The only requirement is the willingness to learn.',
    ],
  },
  {
    id: 'outdoor-catering', date: 'Community', title: 'Our kitchen caters events across Mbala', likes: 27,
    image: 'https://images.pexels.com/photos/14621560/pexels-photo-14621560.jpeg?auto=compress&cs=tinysrgb&w=1600',
    excerpt: 'Hospitality trainees cater weddings, conferences and school functions — real clients, real pressure.',
    body: [
      'There is no better classroom than a real event with real guests waiting to be served. Our hospitality department regularly caters weddings, church functions, conferences and school events around Mbala.',
      'Trainees plan menus, cost ingredients, cook at scale and present food to a paying client — learning the discipline of timing, hygiene and teamwork that turns a good cook into a professional caterer.',
      'The income also supports the department, helping us keep equipment running and fees affordable. If you have an event coming up, our students would be glad to cater it.',
    ],
  },
  {
    id: 'short-courses', date: 'Agriculture', title: 'Short courses in poultry and crops for quick income', likes: 19,
    image: 'https://images.pexels.com/photos/34705724/pexels-photo-34705724.jpeg?auto=compress&cs=tinysrgb&w=1600',
    excerpt: 'Practical short courses in broilers, layers and vegetable production designed to start earning fast.',
    body: [
      'Not everyone can commit to a long programme straight away. Our short courses in poultry and vegetable production are built for people who need a skill — and an income — quickly.',
      'Over a few focused weeks you learn the full cycle: housing and feeding, disease prevention, harvesting and, crucially, how to sell. We treat farming as a business, not just a tradition.',
      'Many graduates start with a small plot or a single batch of broilers and grow from there. We stay in touch through our post-training support to help you keep going.',
    ],
  },
  {
    id: 'fabrication-bay', date: 'Workshop', title: 'A new fabrication and welding bay', likes: 8,
    image: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&q=80&w=1600',
    excerpt: 'Expanded engineering space for metal fabrication, door and window frames, and school furniture.',
    body: [
      'Our engineering department has expanded its fabrication and welding space, giving trainees more room and more stations to practise on.',
      'The bay produces door and window frames, gates, school desks and furniture — products that serve the community while giving learners genuine production experience.',
      'It is part of a steady investment in workshops that match the trades we teach, so graduates leave confident on the same equipment they will meet on the job.',
    ],
  },
  {
    id: 'training-to-business', date: 'Enterprise', title: 'From training to your own business', likes: 31,
    image: 'https://images.pexels.com/photos/34411687/pexels-photo-34411687.jpeg?auto=compress&cs=tinysrgb&w=1600',
    excerpt: 'Graduates receive post-training support and guidance as they start out on their own.',
    body: [
      'A certificate is a beginning, not an ending. MTI’s training cycle deliberately includes post-training support — because the hardest part is often the first months of working for yourself.',
      'We help graduates plan a small enterprise, find their first customers and solve the practical problems that come up. Some form groups; others start solo with a toolbox and a plan.',
      'This is the heart of our mission: rural enterprise development. Skills become livelihoods, and livelihoods build the community.',
    ],
  },
  {
    id: 'school-furniture', date: 'Community', title: 'Desks and furniture for local schools', likes: 14,
    image: 'https://images.pexels.com/photos/29006128/pexels-photo-29006128.jpeg?auto=compress&cs=tinysrgb&w=1600',
    excerpt: 'Our carpentry department builds desks and furniture for schools and offices around the district.',
    body: [
      'Good furniture is in constant demand, and our carpentry and joinery trainees meet it head-on — building desks, chairs, cupboards and office furniture to order.',
      'Each piece is a lesson in measurement, joinery and finishing, and a contribution to schools and businesses that need durable, locally made furniture.',
      'Orders help fund materials and keep training affordable. To commission a piece, reach out through our contact page.',
    ],
  },
];

function Heart({ id, base, big }) {
  const [liked, setLiked] = useState(false);
  useEffect(() => {
    try {
      const arr = JSON.parse(localStorage.getItem(LIKES_KEY) || '[]');
      setLiked(arr.includes(id));
    } catch (e) {}
  }, [id]);
  const toggle = (e) => {
    e.stopPropagation();
    setLiked((prev) => {
      const next = !prev;
      try {
        const arr = JSON.parse(localStorage.getItem(LIKES_KEY) || '[]');
        const set = new Set(arr);
        if (next) set.add(id); else set.delete(id);
        localStorage.setItem(LIKES_KEY, JSON.stringify([...set]));
      } catch (e) {}
      return next;
    });
  };
  return (
    <Like $on={liked} onClick={toggle} aria-pressed={liked} style={big ? { fontSize: '1rem' } : undefined}>
      <span className="heart">{liked ? '♥' : '♡'}</span> {base + (liked ? 1 : 0)} {big ? 'likes' : ''}
    </Like>
  );
}

function News() {
  const [open, setOpen] = useState(null);
  const [items, setItems] = useState(SEED_NEWS);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setOpen(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    fetchNews().then((rows) => {
      if (Array.isArray(rows) && rows.length) {
        setItems(rows.map((r) => ({
          id: r.id,
          date: r.date_label || 'News',
          title: r.title,
          excerpt: r.excerpt || '',
          image: r.image || '',
          likes: r.likes || 0,
          body: (r.body || '').split(/\n\s*\n/).filter(Boolean),
        })));
      }
    }).catch(() => {});
  }, []);

  return (
    <>
      <Navbar />
      <Header>
        <div className="inner">
          <span className="kicker">News &amp; events</span>
          <h1>What’s happening at MTI</h1>
          <p>Intakes, graduations, community projects and updates from around the institute. Tap any story to read more.</p>
        </div>
      </Header>

      <Grid>
        {items.map((n) => (
          <Card key={n.id} $src={n.image} onClick={() => setOpen(n)}>
            <div className="thumb" />
            <div className="b">
              <span className="date">{n.date}</span>
              <h3>{n.title}</h3>
              <p>{n.excerpt}</p>
              <div className="foot">
                <span className="more">Read story →</span>
                <Heart id={n.id} base={n.likes} />
              </div>
            </div>
          </Card>
        ))}
      </Grid>

      <AnimatePresence>
        {open && (
          <Overlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(null)}>
            <Reader $src={open.image} onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.34, ease: theme.ease }}>
              <div className="hero"><button className="x" onClick={() => setOpen(null)} aria-label="Close">×</button></div>
              <div className="body">
                <span className="date">{open.date}</span>
                <h2>{open.title}</h2>
                {open.body.map((para, i) => <p key={i}>{para}</p>)}
                <div className="reactions">
                  <Heart id={open.id} base={open.likes} big />
                  <Link to="/contact" style={{ color: theme.color.accent, fontWeight: 700, fontSize: '0.9rem' }}>Get in touch →</Link>
                </div>
              </div>
            </Reader>
          </Overlay>
        )}
      </AnimatePresence>

      <Footer />
    </>
  );
}

export default News;
